/**
 * Pure runtime resolver for per-element ADF color attributes.
 *
 * **Renderer path (`resolveAdfColor`)** — returns a CSS string the renderer
 * can drop straight into an inline `style`:
 *
 *   • A `var(--hub-color-primary-200)` reference for palette-tracked values
 *     and the day-0 default — the browser cascade resolves these against the
 *     wrapper div emitted by `<HubThemingProvider>`, so brand-color edits
 *     repaint without any React render or JS palette lookup.
 *   • A literal hex (`#FF8B00`) for custom-hex overrides — frozen, won't
 *     track future brand-color edits.
 *
 * **Picker path (`resolveAdfColorForPicker`)** — returns a hex literal so AK
 * `<ColorPicker>` can match against its `knownColors` and highlight the right
 * swatch. The picker can't reverse-look up `var(--hub-...)` strings.
 *
 * Input grammar (both paths):
 *
 *   1. null / undefined                       → day-0 default
 *   2. { custom: hex }                        → custom literal (frozen)
 *   3. `var(--hub-color-primary-200)` string  → palette-tracked (primary form)
 *   4. `"primary.200"` string                 → legacy alias → palette-tracked
 *                                               (kept so ADFs written before
 *                                               the var-ref switch keep working)
 *   5. legacy `var(--hub-primary-color, ...)` → day-0 default (pre-v2 ADF)
 *   6. plain string (raw hex / anything else) → as-is (forward-compat)
 *
 * Pure, synchronous, no side effects → SSR-safe.
 */
import { HUB_COLOR_CSS_VARS } from './hub-css-vars';
import type { AdfColorValue, HubBrandPalette, PaletteRef } from './types';

const PALETTE_REF_PATTERN = /^(primary|accent)\.(100|200|300|400|500|600|700|800|900|1000)$/;
// Matches the canonical `var(--hub-color-{family}-{shade})` form with an
// optional `, <fallback>` segment (a hex snapshot that lets the value resolve
// even outside the wrapper — see `toAdfColor`).
const HUB_COLOR_VAR_PATTERN =
	/^var\(--hub-color-(primary|accent)-(100|200|300|400|500|600|700|800|900|1000)(?:,\s*[^)]+)?\)$/;
const LEGACY_HUB_VAR_PATTERN = /^var\(--hub-primary-/;

const isPaletteRef = (value: string): value is PaletteRef => PALETTE_REF_PATTERN.test(value);
const isHubColorVarRef = (value: string): boolean => HUB_COLOR_VAR_PATTERN.test(value);
const isLegacyHubVarRef = (value: string): boolean => LEGACY_HUB_VAR_PATTERN.test(value);

const cssVarFor = (ref: PaletteRef): string => `var(${HUB_COLOR_CSS_VARS[ref]})`;

/**
 * Extracts the `PaletteRef` carried by a `var(--hub-color-{family}-{shade})`
 * reference string, e.g. `var(--hub-color-primary-200)` → `'primary.200'`.
 * Returns `null` if the string is not a recognised hub-color var ref.
 *
 * Used by the picker path to reverse-look up a swatch hex from a stored var
 * reference.
 */
const paletteRefFromVar = (value: string): PaletteRef | null => {
	const match = value.match(HUB_COLOR_VAR_PATTERN);
	if (!match) {
		return null;
	}
	return `${match[1] as 'primary' | 'accent'}.${match[2] as PaletteRef extends `${string}.${infer S}` ? S : never}` as PaletteRef;
};

/**
 * Renderer-side resolver. Returns a CSS string suitable for an inline
 * `style.backgroundColor` / `borderColor` / etc.
 *
 * Palette-tracked values pass through as CSS-var refs so the browser
 * cascade — not React — repaints when the brand color changes.
 */
export const resolveAdfColor = (value: AdfColorValue, defaultRef: PaletteRef): string => {
	if (value == null) {
		return cssVarFor(defaultRef);
	}
	if (typeof value === 'object') {
		return value.custom;
	}
	if (isHubColorVarRef(value)) {
		// Primary form — already a `var(--hub-color-...)` ref. Pass through.
		return value;
	}
	if (isPaletteRef(value)) {
		// Legacy ADFs written before the var-ref switch persisted bare
		// PaletteRef strings like `"primary.200"`. Map forward.
		return cssVarFor(value);
	}
	if (isLegacyHubVarRef(value)) {
		// Pre-v2 ADF pointing at the legacy `--hub-primary-color` /
		// `--hub-primary-text-color` / `--hub-primary-link-color` chain.
		// `HubThemingProvider` keeps these vars defined on the wrapper with
		// the same values `setHubBrandingStyle` used to write to `<html>`, so
		// we forward the legacy ref verbatim — the browser cascade resolves it
		// to the admin's exact pre-v2 color. Critically we do NOT rewrite to a
		// palette shade (e.g. `primary.200`); the admin's input hex is not at
		// a fixed ramp position so any rewrite would visually drift colors for
		// already-customised hubs.
		return value;
	}
	// Forward-compat: raw hex literals or any other string passed through.
	return value;
};

/**
 * Picker-side resolver. Returns a hex literal so AK `<ColorPicker>` can
 * highlight the swatch the value corresponds to.
 *
 * Identical grammar to `resolveAdfColor`, but resolves through the live
 * palette object rather than emitting a CSS-var reference.
 */
export const resolveAdfColorForPicker = (
	value: AdfColorValue,
	palette: HubBrandPalette,
	defaultRef: PaletteRef,
	primaryColor?: string,
): string => {
	if (value == null) {
		return palette[defaultRef];
	}
	if (typeof value === 'object') {
		return value.custom;
	}
	if (isHubColorVarRef(value)) {
		const ref = paletteRefFromVar(value);
		return ref ? palette[ref] : palette[defaultRef];
	}
	if (isPaletteRef(value)) {
		return palette[value];
	}
	if (isLegacyHubVarRef(value)) {
		// Pre-v2 ADF references resolve at runtime to the admin's exact input
		// hex via `--hub-primary-color` on the wrapper. Returning that exact
		// hex (`primaryColor`) lets the picker match it against its swatch
		// grid and highlight the closest brand-palette circle, keeping the
		// picker UI in sync with the rendered colour. Falls back to the
		// element default if `primaryColor` wasn't passed in.
		return primaryColor ?? palette[defaultRef];
	}
	return value;
};
